import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BellRing, MailCheck, Loader2, Repeat } from "lucide-react";
import { http, errMsg, phone10 } from "@/lib/data";

export const useAlertAssets = () => useQuery({ queryKey: ["alert-assets"], queryFn: () => http.get("/alerts/assets").then((r) => r.data), staleTime: Infinity });

export const AlertButton = ({ q }) => {
  const { data: assets } = useAlertAssets();
  const [open, setOpen] = useState(false);
  const asset = (assets || []).find((a) => a.symbol === q.symbol);
  if (!asset) return null;
  return (
    <>
      <button data-testid={`alert-btn-${q.symbol.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`} onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900"><BellRing className="w-3.5 h-3.5" />Set alert</button>
      {open && <AlertDialog asset={asset} price={q.price} onClose={() => setOpen(false)} />}
    </>
  );
};

const inp = "w-full h-11 rounded-lg border border-slate-300 px-3";

const WhatsAppField = ({ value, onChange }) => (
  <div>
    <input data-testid="alert-whatsapp" inputMode="numeric" maxLength={16} placeholder="WhatsApp number (optional)" value={value} onChange={(e) => onChange(phone10(e.target.value))} className={inp} />
    <p className="text-[11px] text-slate-400 mt-1">Add your number to also get updates on WhatsApp.</p>
  </div>
);

const SummaryPicker = ({ assets, value, onChange }) => (
  <div data-testid="digest-assets" className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1">
    {assets.map((a) => {
      const on = value.includes(a.symbol);
      return <button type="button" key={a.symbol} data-testid={`digest-asset-${a.symbol.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`} onClick={() => onChange(on ? value.filter((x) => x !== a.symbol) : [...value, a.symbol])}
        className={`h-8 px-2.5 rounded-full text-[11px] font-bold border transition-colors ${on ? "bg-[#1E293B] text-white border-[#1E293B]" : "border-slate-300 text-slate-600 hover:border-[#10B981]"}`}>{a.label}</button>;
    })}
  </div>
);

const AlertDialog = ({ asset, price, onClose }) => {
  const { data: assets = [] } = useAlertAssets();
  const [mode, setMode] = useState("target");
  const [v, setV] = useState({ email: "", phone: "", condition: "above", target: price ? +(price * 1.02).toFixed(2) : "", symbols: [asset.symbol] });
  const [st, setSt] = useState({ loading: false, error: "", done: false });
  const submit = async (e) => {
    e.preventDefault();
    if (mode === "summary" && !v.symbols.length) return setSt({ ...st, error: "Pick at least one asset" });
    setSt({ loading: true, error: "", done: false });
    const contact = { email: v.email, phone: v.phone || null };
    try {
      if (mode === "target") await http.post("/alerts", { ...contact, condition: v.condition, symbol: asset.symbol, target: Number(v.target) });
      else await http.post("/digests", { ...contact, symbols: v.symbols });
      setSt({ loading: false, error: "", done: true });
    } catch (err) { setSt({ loading: false, error: errMsg(err), done: false }); }
  };
  const tab = (m, l) => <button type="button" data-testid={`alert-mode-${m}`} onClick={() => setMode(m)} className={`flex-1 h-9 rounded-md text-xs font-bold transition-colors ${mode === m ? "bg-white shadow text-[#0F172A]" : "text-slate-500"}`}>{l}</button>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="alert-dialog" className="bg-white rounded-2xl max-w-md w-[94vw] max-h-[92vh] overflow-y-auto">
        {st.done ? (
          <div data-testid="alert-success" className="text-center py-6"><MailCheck className="w-12 h-12 text-[#10B981] mx-auto" /><p className="font-display text-xl font-bold mt-3">Check your inbox</p><p className="text-sm text-slate-600 mt-1">Click the confirmation link we sent to {v.email} to activate {mode === "target" ? "the alert. We check prices every 15 minutes." : "your price summary. It arrives every 3 days at 8:00 AM IST."}</p><button onClick={onClose} className="mt-5 h-10 px-5 rounded-lg bg-[#1E293B] text-white font-semibold">Done</button></div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><BellRing className="w-5 h-5 text-amber-500" />Price alert · {asset.label}</DialogTitle><DialogDescription>Current price: <b className="font-mono">{price?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b>. {mode === "target" ? "We'll notify you once when your target is hit." : "Get a recurring price summary every 3 days at 8:00 AM IST."}</DialogDescription></DialogHeader>
            <div className="flex gap-1 p-1 rounded-lg bg-slate-100">{tab("target", "Target alert")}{tab("summary", <span className="inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5" />Recurring summary</span>)}</div>
            <form onSubmit={submit} className="space-y-3">
              {mode === "target" ? (
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="alert-condition" value={v.condition} onChange={(e) => setV({ ...v, condition: e.target.value })} className="h-11 rounded-lg border border-slate-300 px-3"><option value="above">Goes above</option><option value="below">Goes below</option></select>
                  <input data-testid="alert-target" required type="number" step="any" min="0" value={v.target} onChange={(e) => setV({ ...v, target: e.target.value })} className="h-11 rounded-lg border border-slate-300 px-3 font-mono" />
                </div>
              ) : <SummaryPicker assets={assets} value={v.symbols} onChange={(symbols) => setV({ ...v, symbols })} />}
              <input data-testid="alert-email" required type="email" placeholder="Your email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} className={inp} />
              <WhatsAppField value={v.phone} onChange={(phone) => setV({ ...v, phone })} />
              {st.error && <p data-testid="alert-error" className="text-sm text-red-600">{st.error}</p>}
              <button data-testid="alert-submit" disabled={st.loading} className="w-full h-11 rounded-lg bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold inline-flex items-center justify-center gap-2">{st.loading && <Loader2 className="w-4 h-4 animate-spin" />}{mode === "target" ? "Create alert" : `Subscribe to ${v.symbols.length} asset${v.symbols.length === 1 ? "" : "s"}`}</button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
